export interface ToolSeedCase {
  key: string;
  label: string;
  description?: string;
  response: unknown;
}

export interface ToolSeed {
  toolName: string;
  schemaVersion: number;
  exampleArguments?: Record<string, unknown>;
  cases: ToolSeedCase[];
}

export interface ToolCatalogEntry {
  name: string;
  seed: ToolSeed | null;
  availableInProject?: boolean;
  parameters?: Record<string, unknown> | null;
}

export interface ToolCatalogResponse {
  tools: ToolCatalogEntry[];
}

export const argChipsForTool = (entry: ToolCatalogEntry): string[] => {
  if (entry.seed?.exampleArguments) {
    return Object.entries(entry.seed.exampleArguments).map(([key, value]) => `${key}: ${JSON.stringify(value)}`);
  }
  const properties = entry.parameters?.properties;
  if (properties && typeof properties === 'object') {
    return Object.keys(properties as Record<string, unknown>);
  }
  return [];
};

export type LeafValue = string | number | boolean | null;

export interface LeafParam {
  path: string;
  value: LeafValue;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const flattenLeafParams = (value: unknown, basePath = ''): LeafParam[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => flattenLeafParams(item, `${basePath}[${index}]`));
  }
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, child]) =>
      flattenLeafParams(child, basePath ? `${basePath}.${key}` : key)
    );
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return basePath ? [{ path: basePath, value }] : [];
  }
  return [];
};

type PathToken = string | number;

const parsePath = (path: string): PathToken[] => {
  const tokens: PathToken[] = [];
  for (const segment of path.split('.')) {
    const match = segment.match(/^([^[\]]*)((?:\[\d+\])*)$/);
    if (!match) {
      tokens.push(segment);
      continue;
    }
    if (match[1]) {
      tokens.push(match[1]);
    }
    const indexes = match[2].match(/\d+/g) ?? [];
    for (const index of indexes) {
      tokens.push(Number(index));
    }
  }
  return tokens;
};

const setAtTokens = (node: unknown, tokens: PathToken[], value: unknown): unknown => {
  if (tokens.length === 0) {
    return value;
  }
  const [head, ...rest] = tokens;
  if (typeof head === 'number') {
    const array = Array.isArray(node) ? [...node] : [];
    array[head] = setAtTokens(array[head], rest, value);
    return array;
  }
  const object = isPlainObject(node) ? { ...node } : {};
  object[head] = setAtTokens(object[head], rest, value);
  return object;
};

export const setLeafParam = (root: unknown, path: string, value: LeafValue): unknown =>
  setAtTokens(root, parsePath(path), value);

export const coerceLeafInput = (raw: string, previous: LeafValue): LeafValue => {
  if (typeof previous === 'number') {
    const parsed = Number(raw);
    return raw.trim() !== '' && Number.isFinite(parsed) ? parsed : previous;
  }
  return raw;
};

export const matchSeedCaseLabel = (seed: ToolSeed | null, value: unknown): string | null => {
  if (!seed) return null;
  const serialized = JSON.stringify(value);
  const matched = seed.cases.find((seedCase) => JSON.stringify(seedCase.response) === serialized);
  return matched?.label ?? null;
};
